import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Briefcase, CheckCircle2, XCircle, Plus, Pencil, X, Save, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PhoneInput from '../components/ui/phone-input';
import { formatDisplay } from '../lib/phone';
import { formatUkDate } from '../lib/date';
import AdminLayout from '../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  quote_requested: { label: 'Quote Requested', tone: 'bg-amber-100 text-amber-800' },
  quote_sent: { label: 'Quote Sent', tone: 'bg-blue-100 text-blue-800' },
  quote_accepted: { label: 'Accepted', tone: 'bg-emerald-100 text-emerald-800' },
  confirmed: { label: 'Confirmed', tone: 'bg-emerald-100 text-emerald-800' },
  in_progress: { label: 'In Progress', tone: 'bg-indigo-100 text-indigo-800' },
  completed: { label: 'Completed', tone: 'bg-gray-100 text-gray-700' },
  cancelled: { label: 'Cancelled', tone: 'bg-red-100 text-red-700' },
  modification_pending: { label: 'Mod. Pending', tone: 'bg-amber-100 text-amber-800' },
};

const INV_PILL = {
  draft:     { label: 'Draft',     tone: 'bg-blue-100 text-blue-800' },
  unpaid:    { label: 'Unpaid',    tone: 'bg-amber-100 text-amber-800' },
  paid:      { label: 'Paid',      tone: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelled', tone: 'bg-gray-100 text-gray-700' },
};

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id = customerId) => {
    try {
      const { data } = await axios.get(`${API}/admin/customers/${encodeURIComponent(id)}`);
      setCustomer(data.customer);
      setBookings(data.bookings || []);
      setInvoices(data.invoices || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const invoiceByBooking = React.useMemo(() => {
    const m = {};
    for (const inv of invoices) if (inv.bookingId) m[inv.bookingId] = inv;
    return m;
  }, [invoices]);
  const downloadInvoice = (invId) => window.open(`${API}/admin/invoices/${invId}/pdf`, '_blank');

  const openEdit = () => {
    setEditForm({
      firstName: customer?.firstName || '',
      lastName:  customer?.lastName || '',
      email:     customer?.email || '',
      phone:     customer?.phone || '',
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/customers/${encodeURIComponent(customerId)}`,
        editForm,
      );
      toast.success(`Customer updated · ${data.bookingsUpdated} booking${data.bookingsUpdated === 1 ? '' : 's'} synced`);
      setShowEdit(false);
      // If email changed, the customer's key changed too — navigate to the new URL
      if (data.id && data.id.toLowerCase() !== customerId.toLowerCase()) {
        navigate(`/admin/customers/${encodeURIComponent(data.id)}`, { replace: true });
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      title={customer ? `${customer.firstName} ${customer.lastName}` : 'Customer'}
      subtitle={customer?.email}
      actions={
        <div className="flex gap-2">
          {customer && (
            <>
              <Button onClick={openEdit} variant="outline" className="border-gray-300" data-testid="customer-edit-btn">
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </Button>
              <Button
                onClick={() => navigate('/admin/dispatch', { state: { prefill: customer } })}
                className="bg-black text-white hover:bg-gray-800"
                data-testid="new-booking-for-customer-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Booking
              </Button>
            </>
          )}
          <Button onClick={() => navigate('/admin/customers')} variant="outline" className="border-gray-300">
            <ArrowLeft className="h-4 w-4 mr-2" /> All Customers
          </Button>
        </div>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-600">Loading…</p>
      ) : !customer ? (
        <p className="text-sm text-gray-700">Customer not found.</p>
      ) : (
        <div className="space-y-6">
          {/* Profile + stats */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <Card className="p-5 lg:col-span-2">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-black text-white flex items-center justify-center text-xl font-bold">
                  {(customer.firstName?.[0] || '?')}{customer.lastName?.[0] || ''}
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-black truncate">
                    {customer.firstName} {customer.lastName}
                  </p>
                  <p className="text-xs text-gray-600 truncate flex items-center gap-1.5 mt-1">
                    <Mail className="h-3.5 w-3.5" />
                    <a href={`mailto:${customer.email}`} className="hover:underline">{customer.email}</a>
                  </p>
                  {customer.phone && (
                    <p className="text-xs text-gray-600 flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5" />
                      <a href={`tel:${customer.phone}`} className="hover:underline">{formatDisplay(customer.phone) || customer.phone}</a>
                    </p>
                  )}
                </div>
              </div>
            </Card>

            <Card className="p-5 text-center">
              <Briefcase className="h-5 w-5 mx-auto text-gray-500 mb-1" />
              <p className="text-3xl font-bold text-black">{customer.totalBookings}</p>
              <p className="text-xs text-gray-500">Total Jobs</p>
            </Card>

            <Card className="p-5 text-center">
              <p className="text-3xl font-bold text-black">£{Number(customer.totalSpend || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Lifetime Value</p>
              <div className="flex justify-center gap-3 mt-3 text-xs">
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" /> {customer.completedBookings}
                </span>
                <span className="flex items-center gap-1 text-red-700">
                  <XCircle className="h-3 w-3" /> {customer.cancelledBookings}
                </span>
              </div>
            </Card>
          </div>

          {/* Job history */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-black">Job History</h2>
                <p className="text-xs text-gray-500">All bookings for this customer, newest first</p>
              </div>
            </div>

            {bookings.length === 0 ? (
              <p className="text-sm text-gray-500">No bookings yet.</p>
            ) : (
              <div className="divide-y divide-gray-100" data-testid="customer-jobs-list">
                {bookings.map((b) => {
                  const meta = STATUS_META[b.status] || STATUS_META.confirmed;
                  const inv = invoiceByBooking[b.id];
                  const invMeta = inv ? (INV_PILL[inv.status] || INV_PILL.unpaid) : null;
                  return (
                    <div
                      key={b.id}
                      className="w-full text-left py-4 grid grid-cols-12 gap-3 items-center hover:bg-gray-50 px-2 rounded-md cursor-pointer"
                      onClick={() => navigate('/admin/bookings')}
                      data-testid={`customer-job-${b.id}`}
                    >
                      <div className="col-span-2">
                        <p className="text-xs uppercase text-gray-500 tracking-wider">{b.serviceType?.split(' ')[0]}</p>
                        <p className="font-semibold text-black text-sm">{b.pickupTime}</p>
                        <p className="text-xs text-gray-600 flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {formatUkDate(b.pickupDate)}
                        </p>
                      </div>
                      <div className="col-span-4 min-w-0">
                        <p className="text-sm text-black font-medium truncate flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-gray-400" />
                          {b.pickupLocation}
                        </p>
                        <p className="text-xs text-gray-600 truncate pl-5">&rarr; {b.dropoffLocation}</p>
                      </div>
                      <div className="col-span-2 text-sm font-bold text-black">
                        {b.quoteAmount ? `£${Number(b.quoteAmount).toFixed(0)}` : '—'}
                      </div>
                      <div className="col-span-2 flex">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${meta.tone}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="col-span-2 flex justify-end" onClick={(e) => e.stopPropagation()}>
                        {inv ? (
                          <button
                            type="button"
                            onClick={() => downloadInvoice(inv.id)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-300 rounded-md px-2.5 py-1.5 hover:bg-black hover:text-white hover:border-black transition-colors"
                            title={`${inv.id} · £${Number(inv.amount || 0).toFixed(2)}`}
                            data-testid={`customer-invoice-${b.id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${invMeta.tone}`}>{invMeta.label}</span>
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-400">No invoice</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="customer-edit-modal">
          <Card className="max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-black">Edit Customer</h3>
                <p className="text-xs text-gray-500">Changes apply across all of this customer's bookings.</p>
              </div>
              <button onClick={() => setShowEdit(false)} className="text-gray-400 hover:text-black p-1" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-sm font-medium text-gray-900">First Name</Label>
                <Input
                  className="mt-1"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  data-testid="edit-customer-firstName"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-900">Last Name</Label>
                <Input
                  className="mt-1"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  data-testid="edit-customer-lastName"
                />
              </div>
            </div>

            <div className="mb-3">
              <Label className="text-sm font-medium text-gray-900">Email</Label>
              <Input
                type="email"
                className="mt-1"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-customer-email"
              />
            </div>

            <div className="mb-5">
              <Label className="text-sm font-medium text-gray-900">Phone</Label>
              <div className="mt-1">
                <PhoneInput
                  value={editForm.phone}
                  onChange={(v) => setEditForm({ ...editForm, phone: v })}
                  data-testid="edit-customer-phone"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEdit(false)} className="border-gray-300">
                Cancel
              </Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-black text-white hover:bg-gray-800" data-testid="edit-customer-save">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default CustomerDetail;
